import { SVLoader, SVTabs } from '@/components/core';
import DefaultLayout from '@/components/layouts/DefaultLayout';
import ScheduledTransactionsList from '@/components/scheduledTransactions/ScheduledTransactionsList';
import { ROUTES } from '@/constants/routes';
import { useGetBankAccountDetails } from '@/hooks/requests/useBankAccounts';
import { useGetScheduledTransactionList } from '@/hooks/requests/useTransactions';
import { ScheduledTransactionStatus } from '@/types/transactions';
import { generateBankAccountDisplayName } from '@/utils/bankAccounts';
import { Card, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';

export default function ScheduledTransactionsListPage() {
  const { uuid = '' } = useParams();
  const { accountDetails, accountsDetailsIsPending } =
    useGetBankAccountDetails(uuid);
  const { aggregateInfo, isScheduledTransactionListPending } =
    useGetScheduledTransactionList(uuid, { page: 0 });

  if (accountsDetailsIsPending || isScheduledTransactionListPending) {
    return (
      <DefaultLayout header="Scheduled Transactions">
        <Card>
          <SVLoader />
        </Card>
      </DefaultLayout>
    );
  }

  if (!accountDetails) {
    return (
      <DefaultLayout header="Scheduled Transactions">
        <Card>
          <Typography>
            Something went wrong retreiving the scheduled transactions. Our team
            has been notified.
          </Typography>
        </Card>
      </DefaultLayout>
    );
  }

  const { applicationType, business, primaryOwner, owners } = accountDetails;
  const { fullName, email } = primaryOwner.profile;
  const { name: businessName } = business || {};
  const totalOwners = owners.length;
  const bankAccountName = generateBankAccountDisplayName({
    applicationType,
    businessName,
    fullName,
    email,
    totalOwners,
  });

  return (
    <DefaultLayout
      header="Scheduled Transactions"
      breadcrumb={[
        { label: 'Bank Accounts', href: ROUTES.BANK_ACCOUNTS },
        { label: bankAccountName, href: `${ROUTES.BANK_ACCOUNTS}/${uuid}` },
        { label: 'Scheduled Transactions', isActive: true },
      ]}>
      <Card>
        <SVTabs
          headers={[
            `Active (${aggregateInfo?.totalActive || 0})`,
            `Paused (${aggregateInfo?.totalPaused || 0})`,
            `Canceled (${aggregateInfo?.totalCanceled || 0})`,
            `Completed (${aggregateInfo?.totalCompleted || 0})`,
          ]}
          panels={[
            <ScheduledTransactionsList
              key="active"
              bankAccountId={uuid}
              status={ScheduledTransactionStatus.ACTIVE}
            />,
            <ScheduledTransactionsList
              key="paused"
              bankAccountId={uuid}
              status={ScheduledTransactionStatus.PAUSED}
            />,
            <ScheduledTransactionsList
              key="canceled"
              bankAccountId={uuid}
              status={ScheduledTransactionStatus.CANCELED}
            />,
            <ScheduledTransactionsList
              key="completed"
              bankAccountId={uuid}
              status={ScheduledTransactionStatus.COMPLETED}
            />,
          ]}
        />
      </Card>
    </DefaultLayout>
  );
}
